import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { RefObject, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { gray } from "../../theme/themePrimitives";
import "./scormApi";
import {
  redirectToHome,
  setAssignmentId,
  setPortalId,
  setUserId,
  setUsername,
} from "./scormApi";
import { usePortal } from "../../context/PortalsContext";
import { useTranslate } from "../../hooks/useTranslate";

// declare global {
//   interface Window {
//     API?: {
//       LMSSetValue: (key: string, value: string) => string;
//       initialized: boolean;
//       LMSCommit: () => string;
//       LMSGetValue: (key: string) => string;
//       // Add other SCORM API methods if needed
//     };
//     API_1484_11?: {
//       GetValue: (key: string) => string;
//       SetValue: (key: string, value: string) => string;
//       Commit: () => string;
//       data: {};
//       initialized: boolean;
//       // Add other SCORM 2004 API methods if needed
//     };
//   }
// }

type ScormPlayerProps = {
  assignmentId: string;
  setMaxChapter: React.Dispatch<React.SetStateAction<string>>;
  maxChapter: RefObject<string>;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  assignmentUrl: string;
  fillParent?: boolean;
  onAttemptsExceededChange?: (exceeded: boolean) => void;
};

const ScormPlayer = ({
  assignmentId,
  setMaxChapter,
  maxChapter,
  iframeRef,
  assignmentUrl,
  fillParent = false,
  onAttemptsExceededChange,
}: ScormPlayerProps) => {
  const { auth } = useAuth();
  const { portals } = usePortal();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const t = useTranslate("assignment-detail");
  const [isCorruptedCourse, setIsCorruptedCourse] = useState(false);
  // Covers the blank flash while the SCORM iframe navigates to a new page —
  // triggered either by us (jump-to-chapter forces a full iframe reload so
  // the course re-reads cmi.location on init) or by the course's own
  // internal next/prev, which we don't control either way.
  const [isNavigating, setIsNavigating] = useState(true);
  const [attemptsExceeded, setAttemptsExceeded] = useState(false);
  const unloadCleanupRef = useRef<(() => void) | null>(null);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const showToastRef = useRef(showToast);
  const navigateRef = useRef(navigate);
  const onAttemptsExceededChangeRef = useRef(onAttemptsExceededChange);
  showToastRef.current = showToast;
  navigateRef.current = navigate;
  onAttemptsExceededChangeRef.current = onAttemptsExceededChange;

  useEffect(() => {
    onAttemptsExceededChangeRef.current?.(attemptsExceeded);
  }, [attemptsExceeded]);

  useEffect(() => {
    let originalSetValue: ((key: string, value: string) => string) | undefined;
    let originalLMSSetValue:
      | ((key: string, value: string) => string)
      | undefined;

    // Setup wrapper for SCORM 2004
    if (window.API_1484_11) {
      originalSetValue = window.API_1484_11.SetValue;

      const modifiedSetValue = (key: string, value: string) => {
        const result = originalSetValue!(key, value);
        if (key === "cmi.location") {
          const progressValue = value.split(",")[0];
          const currentMax = parseInt(maxChapter.current || "0", 10);
          const newProgress = parseInt(progressValue, 10);
          if (newProgress > currentMax) {
            setMaxChapter(progressValue);
          }
        }

        return result;
      };

      window.API_1484_11.SetValue = modifiedSetValue;
    }

    // Setup wrapper for SCORM 1.2
    if (window.API) {
      originalLMSSetValue = window.API.LMSSetValue;

      const modifiedLMSSetValue = (key: string, value: string) => {
        const result = originalLMSSetValue!(key, value);
        if (key === "cmi.core.lesson_location") {
          const progressValue = value.split(",")[0];
          const currentMax = parseInt(maxChapter.current || "0", 10);
          const newProgress = parseInt(progressValue, 10);
          if (newProgress > currentMax) {
            setMaxChapter(progressValue);
          }
        }

        return result;
      };

      window.API.LMSSetValue = modifiedLMSSetValue;
    }

    return () => {
      // Restore original functions on cleanup
      if (window.API_1484_11 && originalSetValue) {
        window.API_1484_11.SetValue = originalSetValue;
      }
      if (window.API && originalLMSSetValue) {
        window.API.LMSSetValue = originalLMSSetValue;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Depend on primitive values only so context object reference changes don't re-run this every render (avoids update loop)
  const userId = auth.user?.UserPortalUid;
  const portalUid = portals.selectedPortal?.portalUid;
  const userDisplayName =
    auth.user != null ? `${auth.user.firstName} ${auth.user.lastName}` : "";

  useEffect(() => {
    if (userId && portalUid) {
      setUserId(userId);
      setUsername(userDisplayName);
      setPortalId(portalUid);
      setAssignmentId(assignmentId);
    }
  }, [userId, portalUid, assignmentId, userDisplayName]);

  useEffect(() => {
    const handleAttemptsExceeded = () => {
      setAttemptsExceeded(true);
    };
    window.addEventListener(
      "assignmentAttemptsExceeded",
      handleAttemptsExceeded,
    );
    return () => {
      window.removeEventListener(
        "assignmentAttemptsExceeded",
        handleAttemptsExceeded,
      );
      setAttemptsExceeded(false);
    };
  }, []);

  const attachUnloadListener = () => {
    unloadCleanupRef.current?.();
    unloadCleanupRef.current = null;
    try {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      const handleBeforeUnload = () => setIsNavigating(true);
      win.addEventListener("beforeunload", handleBeforeUnload);
      unloadCleanupRef.current = () =>
        win.removeEventListener("beforeunload", handleBeforeUnload);
    } catch {}
  };

  const handleIframeLoad = () => {
    setIsNavigating(false);
    attachUnloadListener();
  };

  useEffect(() => {
    return () => unloadCleanupRef.current?.();
  }, []);

  useEffect(() => {
    setIsCorruptedCourse(false);
    setIsNavigating(true);
    setAttemptsExceeded(false);
    if (!assignmentUrl) {
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        if (redirectTimeoutRef.current) {
          clearTimeout(redirectTimeoutRef.current);
          redirectTimeoutRef.current = null;
        }
      };
    }

    const POLL_INTERVAL_MS = 200;
    const POLL_MAX_MS = 5000;
    let elapsed = 0;

    const checkFor404 = (): boolean => {
      try {
        const iframe = iframeRef.current;
        if (!iframe?.contentWindow) return false;
        const win = iframe.contentWindow;
        const pathname = win.location?.pathname ?? "";
        if (
          pathname === "/error/404" ||
          pathname.endsWith("/error/404") ||
          pathname === "/error/401" ||
          pathname.endsWith("/error/401") ||
          pathname === "/error/403" ||
          pathname.endsWith("/error/403")
        ) {
          return true;
        }
        const doc = iframe.contentDocument;
        const bodyText = doc?.body?.innerText ?? "";
        if (
          bodyText.includes("Page not found") ||
          bodyText.includes("Access denied")
        ) {
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };

    pollIntervalRef.current = setInterval(() => {
      elapsed += POLL_INTERVAL_MS;
      if (checkFor404()) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsCorruptedCourse(true);
        showToastRef.current("Empty or corrupted course", "error");
        redirectTimeoutRef.current = setTimeout(() => {
          navigateRef.current(-1);
        }, 3000);
      } else if (elapsed >= POLL_MAX_MS) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
    };
    // Only re-run when assignmentUrl changes; iframeRef is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentUrl]);

  const IFRAME_HEIGHT = fillParent ? "100%" : 1000;

  const attemptsExceededOverlay = attemptsExceeded && (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(16, 24, 40, 0.65)",
        p: 3,
      }}
    >
      <Stack
        sx={{
          width: "529px",
          alignItems: "center",
          textAlign: "center",
          gap: 2,
          backgroundColor: "#fff",
          borderRadius: "16px",
          boxShadow: "0px 8px 24px rgba(16, 24, 40, 0.25)",
          p: "24px",
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#FEF0C7",
            boxShadow: "0px 1px 2px 0px #1018280D",
          }}
        >
          <InfoOutlinedIcon sx={{ color: "#DC6803", fontSize: 28 }} />
        </Box>
        <Typography sx={{ fontWeight: 600, fontSize: 18, color: gray[900] }}>
          {t("modalMaxAttemptsLimit", "title")}
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: "22px", color: gray[600] }}>
          {t("modalMaxAttemptsLimit", "description")}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => redirectToHome()}
          sx={{
            mt: 1,
            borderRadius: "8px",
            color: gray[900],
            borderColor: gray[300],
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          {t("modalMaxAttemptsLimit", "goBack")}
        </Button>
      </Stack>
    </Box>
  );

  if (isCorruptedCourse) {
    return (
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          position: "relative",
          height: IFRAME_HEIGHT,
          overflow: "hidden",
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        position: "relative",
        height: IFRAME_HEIGHT,
        overflow: "hidden",
      }}
    >
      {assignmentUrl && (
        <>
          <Box
            component="iframe"
            src={`/${assignmentUrl}`}
            ref={iframeRef as React.RefObject<HTMLIFrameElement>}
            key="scorm-assignment"
            width="100%"
            onLoad={handleIframeLoad}
            sx={{
              border: "none",
              display: "block",
              height: IFRAME_HEIGHT,
            }}
            title="SCORM Assignment"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-top-navigation"
            referrerPolicy="no-referrer"
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              opacity: isNavigating ? 1 : 0,
              pointerEvents: isNavigating ? "auto" : "none",
              transition: "opacity 150ms ease",
            }}
          >
            <CircularProgress size={48} />
          </Box>
        </>
      )}
      {attemptsExceededOverlay}
    </Box>
  );
};

export default ScormPlayer;
